import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { ProjectCode } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { CampaignStatus } from '@hooks-api/types/dataModel/campaigns'
import { ThemeProvider } from '@pets-ui/ThemeProvider/ThemeProvider'
import { colorTheme as aquaColorTheme } from '@pets-ui/themes/aqua'
import { colorTheme as dcsColorTheme } from '@pets-ui/themes/dcs'
import { colorTheme as fastpassColorTheme } from '@pets-ui/themes/fastpass'
import { colorTheme as hmColorTheme } from '@pets-ui/themes/hm'
import { colorTheme as lmColorTheme } from '@pets-ui/themes/lm'
import { colorTheme as mnpColorTheme } from '@pets-ui/themes/mnp'
import { colorTheme as pmColorTheme } from '@pets-ui/themes/pm'
import { colorTheme as ppColorTheme } from '@pets-ui/themes/pp'
import { colorTheme as ytColorTheme } from '@pets-ui/themes/yt'
import { type Colors } from '@pets-ui/tokens/colors.stylex'
import { type Theme } from '@stylexjs/stylex'
import { Suspense, useEffect } from 'react'
import { PopupWrapper } from '../../../components/PopupWrapper'
import { MerchantClosed } from '../MerchantClosed/MerchantClosed'
import { AuthRoutes } from './AuthRoutes'
import { UnauthRoutes } from './UnauthRoutes'

const colorThemeMap: Partial<Record<ProjectCode, Theme<Colors>>> = {
  [ProjectCode.DCS]: dcsColorTheme,
  [ProjectCode.HM]: hmColorTheme,
  [ProjectCode.LM]: lmColorTheme,
  [ProjectCode.MNP]: mnpColorTheme,
  [ProjectCode.AQUA]: aquaColorTheme,
  [ProjectCode.PP]: ppColorTheme,
  [ProjectCode.PM]: pmColorTheme,
  [ProjectCode.YT]: ytColorTheme,
}

export function MerchantRouters() {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { isLoading: isActivityLoading, destructuredData: activityInfo } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const isCampaignClosed = activityInfo?.status && [CampaignStatus.EXPIRED, CampaignStatus.NOT_YET, CampaignStatus.PAUSED].includes(activityInfo?.status)

  const { destructuredData: campaignMember, isLoading: isCampaignMemberLoading } = useApiCampaignMember({ code: campaignCodeFromUrl }, {
    shouldRetryOnError: (error) => {
      if (error?.response?.data.error?.type === 'AUTHENTICATION_ERROR') {
        return false
      }
      return true
    },
  })

  useEffect(() => {
    if (campaignMember) {
      datadogRum.setUser({
        id: campaignMember.platform_uid,
        name: campaignMember.user_name,
        email: campaignMember.account,
        project_code: campaignMember.project_code,
        roles: campaignMember.roles,
      })
    }
  }, [campaignMember])

  if (isActivityLoading || isCampaignMemberLoading) return <LoaderFullPage />

  if (!activityInfo || isCampaignClosed) {
    return (
      <MerchantClosed />
    )
  }

  // @ts-expect-error - TODO: fix this
  const projectCode = activityInfo.brand_codes_info[0].project_code
  const colorTheme = colorThemeMap[projectCode] || fastpassColorTheme

  return (
    <ThemeProvider theme={colorTheme}>
      <Suspense>
        <PopupWrapper projectCode={projectCode} campaignId={activityInfo.campaign_id} countryCode={activityInfo.region} />
      </Suspense>
      {campaignMember ? <AuthRoutes /> : <UnauthRoutes />}
    </ThemeProvider>
  )
}
